import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../lib/redux/hooks";
import { setLegalRepresentativeCreateRequestField, setLegalRepresentativeUpdateRequestField } from "../../../../pages/clienti/feature/legalRepresentative/slice";
import { genreSelect, OptionType, Province, Region, regioniProvinceMap, regions } from "../../../../utils";
import { provinceComuniMap } from "../../../../utils/listaComuni";
import { Calendar } from "../../calendar";
import { MadfarmInput } from "../../input/newInput";
import { MadfarmStaticSelect } from "../../select/newSelect/static";

interface Props {
    mode: "create" | "edit";
}

export function RappLegaleAnagrafica({ mode }: Props) {
    const legalRapresentativeState = useAppSelector(state => state.legalRapresentative)
    const dispatch = useAppDispatch()

    const [provinceOptions, setProvinceOptions] = useState<OptionType[]>([])
    const [selectedRegion, setSelectedRegion] = useState<OptionType | null>(null)
    const [comuneOptions, setComuneOptions] = useState<OptionType[]>([])
    const [selectedProvince, setSelectedProvince] = useState<OptionType | null>(null)

    const regionOptions: OptionType[] = Array.from(regioniProvinceMap.keys()).map(r => ({ value: r, label: r }))

    const handleRegionChange = (opt: OptionType | null) => {
        setSelectedRegion(opt)
        setSelectedProvince(null)
    }
    const handleProvinceChange = (opt: OptionType | null) => setSelectedProvince(opt)

    const legalRepresentativeRequest = mode === "create" ? legalRapresentativeState.legalRepresentativeCreateRequest : legalRapresentativeState.legalRepresentativeUpdateRequest

    useEffect(() => {
        if (selectedRegion || legalRepresentativeRequest.countryRegion) {
            const region = selectedRegion ? selectedRegion.toString() : legalRepresentativeRequest.countryRegion?.toString() || null
            const found = regions.find((r: Region) => r.name === region)
            setProvinceOptions(found ? found.provinces.map((p: Province) => ({ value: p.code, label: p.name })) : [])
        } else {
            setProvinceOptions([])
        }
    }, [selectedRegion, legalRepresentativeRequest.countryRegion])

    useEffect(() => {
        if (selectedProvince || legalRepresentativeRequest.province) {
            const province = selectedProvince ? selectedProvince.toString() : legalRepresentativeRequest.province?.toString() || null
            const comuni = provinceComuniMap.get(province!) || []
            setComuneOptions(comuni.map((c: string) => ({ value: c, label: c })))
        } else {
            setComuneOptions([])
        }
    }, [selectedProvince, legalRepresentativeRequest.province])


    return (
        <div className="flex flex-col gap-4">
            <MadfarmInput
                required
                label="Codice Fiscale"
                placeholder="Codice Fiscale"
                supportingText={legalRapresentativeState.legalRepresentativeCreateErrors.fiscalCode}
                error={!!legalRapresentativeState.legalRepresentativeCreateErrors.fiscalCode}
                defaultValue={legalRepresentativeRequest.fiscalCode}
                onChangeText={(text) => {
                    if (mode === "create")
                        dispatch(setLegalRepresentativeCreateRequestField({ field: "fiscalCode", value: text }))
                    else
                        dispatch(setLegalRepresentativeUpdateRequestField({ field: "fiscalCode", value: text }))
                }}
            />
            <div className="grid grid-cols-2 gap-4">
                <MadfarmInput
                    required
                    label="Cognome"
                    placeholder="Cognome"
                    supportingText={legalRapresentativeState.legalRepresentativeCreateErrors.surname}
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.surname}
                    defaultValue={legalRepresentativeRequest.surname}
                    onChangeText={(text) => {
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "surname", value: text }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "surname", value: text }))
                    }}
                />
                <MadfarmInput
                    required
                    label="Nome"
                    placeholder="Nome"
                    supportingText={legalRapresentativeState.legalRepresentativeCreateErrors.name}
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.name}
                    defaultValue={legalRepresentativeRequest.name}

                    onChangeText={(text) => {
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "name", value: text }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "name", value: text }))
                    }}
                />
            </div>
            <div className="grid grid-cols-2 gap-4">
                <MadfarmStaticSelect
                    customZIndex={9999}
                    required
                    label="Sesso"
                    placeholder="Sesso"
                    noOptionsMessage="Nessuna opzione"
                    options={genreSelect}
                    value={legalRepresentativeRequest.sex}
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.sex}
                    errorLabel={legalRapresentativeState.legalRepresentativeCreateErrors.sex}
                    onChange={(value) => {
                        // @ts-ignore
                        const newValue = value !== null ? value.value : null
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "sex", value: newValue }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "sex", value: newValue }))
                    }}
                />
                <Calendar
                    required
                    label="Data di nascita"
                    placeholder="gg/mm/yyyy"
                    errorLabel={legalRapresentativeState.legalRepresentativeCreateErrors.birthdate}
                    selected={legalRepresentativeRequest.birthdate}
                    onChange={(date) => {
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "birthdate", value: format(new Date(date), 'yyyy-MM-dd') }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "birthdate", value: format(new Date(date), 'yyyy-MM-dd') }))
                    }}
                />
            </div>
            <div className="grid grid-cols-3 gap-4">
                <MadfarmStaticSelect
                    customZIndex={9999}
                    required
                    label="Regione"
                    noOptionsMessage="Nessuna regione trovata"
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.countryRegion}
                    errorLabel={legalRapresentativeState.legalRepresentativeCreateErrors.countryRegion}
                    value={legalRepresentativeRequest.countryRegion}
                    placeholder="Regione"
                    options={regionOptions}
                    onChange={(value) => {
                        // @ts-ignore
                        const newValue = value !== null ? value.value : null
                        handleRegionChange(newValue as unknown as OptionType | null)
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "countryRegion", value: newValue }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "countryRegion", value: newValue }))
                    }}
                />
                <MadfarmStaticSelect
                    customZIndex={9999}
                    required
                    label="Provincia"
                    noOptionsMessage="Nessuna provincia trovata"
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.province}
                    errorLabel={legalRapresentativeState.legalRepresentativeCreateErrors.province}
                    value={legalRepresentativeRequest.province}
                    placeholder="Provincia"
                    options={provinceOptions}
                    onChange={(value) => {
                        // @ts-ignore
                        const newValue = value !== null ? value.value : null
                        handleProvinceChange(newValue as unknown as OptionType | null)
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "province", value: newValue }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "province", value: newValue }))
                    }}
                />
                <MadfarmStaticSelect
                    customZIndex={9999}
                    required
                    label="Comune"
                    noOptionsMessage="Nessun comune trovato"
                    error={!!legalRapresentativeState.legalRepresentativeCreateErrors.city}
                    errorLabel={legalRapresentativeState.legalRepresentativeCreateErrors.city}
                    placeholder="Comune"
                    value={legalRepresentativeRequest.city}
                    options={comuneOptions}
                    onChange={(value) => {
                        // @ts-ignore
                        const newValue = value !== null ? value.value : null
                        if (mode === "create")
                            dispatch(setLegalRepresentativeCreateRequestField({ field: "city", value: newValue }))
                        else
                            dispatch(setLegalRepresentativeUpdateRequestField({ field: "city", value: newValue }))
                    }}
                />
            </div>
        </div>
    )
}